import { axios } from './axios'
import { Order, Sender, Payee, User, ExchangeRate, CurrencyEnum, OrderUpdate, SenderUpdate, PayeeUpdate, SenderTypeEnum, SalesPerson } from '../store/types'
import { extractOrderUpdateFields, extractSenderUpdateFields, extractPayeeUpdateFields } from '../utils/api-helpers'

const basePath = '/api'

export const api = {
  auth: {
    login: async (email: string, password: string): Promise<void> => {
      await axios.post(`${basePath}/auth/login`, { email, password })
    },
    signup: async (email: string, password: string, sales_person_id: number): Promise<void> => {
      await axios.post(`${basePath}/auth/signup`, { email, password, sales_person_id })
    },
    me: async (): Promise<User> => {
      const response = await axios.get(`${basePath}/user/me`)
      return response.data
    }
  },
  salesPerson: {
    fetch: async (): Promise<SalesPerson> => {
      const response = await axios.get(`${basePath}/sales-person/me`)
      return response.data
    },
    fetchAll: async (): Promise<SalesPerson[]> => {
      const response = await axios.get(`${basePath}/sales-person`)
      return response.data
    }
  },
  orders: {
    fetch: async (): Promise<Order[]> => {
      const response = await axios.get(`${basePath}/orders`)
      return response.data
    },
    fetchById: async (orderId: string): Promise<Order> => {
      const response = await axios.get(`${basePath}/orders/${orderId}`)
      return response.data
    },
    create: async (order: Order): Promise<Order> => {
      const response = await axios.post(`${basePath}/orders`, order)
      return response.data
    },
    update: async (orderId: number, order: Partial<Order>): Promise<Order> => {
      const updateData = extractOrderUpdateFields(order)
      const response = await axios.patch(`${basePath}/orders/${orderId}`, updateData)
      return response.data
    },
    assignSender: async (orderId: string | number, senderId: string | number): Promise<Sender> => {
      const response = await axios.post(`${basePath}/orders/${orderId}/assign-sender/${senderId}`)
      return response.data
    },
    assignPayee: async (orderId: string | number, payeeId: string | number): Promise<Order> => {
      const response = await axios.post(`${basePath}/orders/${orderId}/assign-payee/${payeeId}`)
      return response.data
    }
  },
  senders: {
    fetch: async (): Promise<Sender[]> => {
      const response = await axios.get(`${basePath}/senders`)
      return response.data
    },
    fetchById: async (senderId: number): Promise<Sender> => {
      const response = await axios.get(`${basePath}/senders/${senderId}`)
      return response.data
    },
    create: async (sender: Sender): Promise<Sender> => {
      const response = await axios.post(`${basePath}/senders`, sender)
      return response.data
    },
    update: async (senderId: number, sender: Sender): Promise<Sender> => {
      const updateData = extractSenderUpdateFields(sender)
      const response = await axios.patch(`${basePath}/senders/${senderId}`, updateData)
      return response.data
    },
    delete: async (senderId: number): Promise<void> => {
      await axios.delete(`${basePath}/senders/${senderId}`)
    },
    parseSenderDocument: async (senderType: SenderTypeEnum, file: File): Promise<Sender> => {
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await axios.post(`${basePath}/parsing/sender/${senderType}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    }
  },
  payees: {
    fetch: async (): Promise<Payee[]> => {
      const response = await axios.get(`${basePath}/payees`)
      return response.data
    },
    fetchById: async (payeeId: number): Promise<Payee> => {
      const response = await axios.get(`${basePath}/payees/${payeeId}`)
      return response.data
    },
    create: async (payee: Payee): Promise<Payee> => {
      const response = await axios.post(`${basePath}/payees`, payee)
      return response.data
    },
    update: async (payeeId: number, payee: Partial<Payee>): Promise<Payee> => {
      const updateData = extractPayeeUpdateFields(payee)
      const response = await axios.patch(`${basePath}/payees/${payeeId}`, updateData)
      return response.data
    },
    parsePayeeDocument: async (file: File): Promise<Payee> => {
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await axios.post(`${basePath}/parsing/payee`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    }
  },
  exchangeRates: {
    fetch: async (): Promise<ExchangeRate[]> => {
      const response = await axios.get(`${basePath}/currency/exchange-rates`)
      const ratesObject = response.data.rates || response.data
      
      return Object.keys(ratesObject)
        .filter(currency => Object.values(CurrencyEnum).includes(currency as CurrencyEnum))
        .map(currency => ({
          currency: currency as CurrencyEnum,
          rate: parseFloat(ratesObject[currency].toFixed(4)),
          flag: currency
        }))
    }
  }
} 